import * as React from "react";
import * as __intl__ from "@frameio/i18n";
import { type TranscriptionSource } from "@frameio/graphql-client/generated";
export type Text = {
    addLanguageBody: string;
    addLanguageTitle: string;
    addLanguageNote: string;
    addLanguagePlaceholder: string;
    body: string;
    buttonLabel: string;
    cancel: string;
    clearSearch: string;
    disabledByAdministrator: string;
    speakerLabelingUnavailable: string;
    speakerDiarizationDisabledTooltip: string;
    errorBody: string;
    errorTitle: string;
    feedback: string;
    captionsUploadBody: string;
    generationCompletionTimeBody: () => string | React.ReactNode;
    generatingTitle: (source?: TranscriptionSource | undefined) => string | React.ReactNode;
    loadingLabel: string;
    labelSpeakers: string;
    transcriptsDisclaimer: () => string | React.ReactNode;
    captionsDisclaimer: string;
    retry: string;
    title: string;
    noAudioWarning: string;
};
export type Deps = {
    Break: any;
};
export default {
    DEFINE(_messages: Text, { Break }: Deps): void {
        return Object.defineProperties(this, {
            /** Body text for add-language view. */
            addLanguageBody: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.addLanguageBody?c=2ua170cbc149z", defaultMessage: "Select a different language to generate a new Transcript & Captions for your asset." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title for add-language view. */
            addLanguageTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.addLanguageTitle?c=1a893p3udhgfl", defaultMessage: "Add Transcript Language" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Note explaining language selection limitations. */
            addLanguageNote: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.addLanguageNote?c=3v8h8lyo98dox", defaultMessage: "Transcripts will not be translated. Accuracy will be dependent on selected language matching spoken language." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Placeholder for the language dropdown. */
            addLanguagePlaceholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.addLanguagePlaceholder?c=1qe2icgth51d2", defaultMessage: "Choose language" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Body text for the default view. */
            body: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.body?c=21qkvvubnufp9", defaultMessage: "Transcribe and caption your asset to streamline navigation and feedback." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Primary action button label. */
            buttonLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.buttonLabel?c=2vh4mbm917h71", defaultMessage: "Transcribe" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Cancel button label. */
            cancel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.cancel?c=2ojhd8rzicmo0", defaultMessage: "Cancel" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Clear search button label. */
            clearSearch: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.clearSearch?c=3jc0a339trb2c", defaultMessage: "Clear language search" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip copy when transcription is disabled. */
            disabledByAdministrator: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.disabledByAdministrator?c=d5xi7sv9ht3m", defaultMessage: "Disabled by your Administrator" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Notice when speaker labeling is unavailable. */
            speakerLabelingUnavailable: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.speakerLabelingUnavailable?c=zl8vl1kb02aj", defaultMessage: "Speaker labeling not available" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip for disabled speaker diarization. */
            speakerDiarizationDisabledTooltip: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.speakerDiarizationDisabledTooltip?c=3rwbwvraie8jm", defaultMessage: "Unavailable in your location" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error body shown when transcription fails. */
            errorBody: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.errorBody?c=2y233o4t2j273", defaultMessage: "An error occurred while transcribing and generating captions for this asset. Please try again later." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error title shown when transcription fails. */
            errorTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.errorTitle?c=3hysb3djkf1u6", defaultMessage: "Something went wrong" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feedback action label. */
            feedback: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.feedback?c=3ooxlga8hwjwg", defaultMessage: "Submit feedback" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Body text while uploading captions. */
            captionsUploadBody: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.captionsUploadBody?c=19l0bcv5qmxzx", defaultMessage: "You can close this panel during upload." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Body text while generating transcript and captions. */
            generationCompletionTimeBody: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.generationCompletionTimeBody?c=2h3rw9z104f2d", defaultMessage: "<jsx>Completion time will depend on asset length. <Break></Break> You can close this panel during generation.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), Break: (__children__: React.ReactNode) => <Break>{React.Children.toArray(__children__)}</Break> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title text while generating or uploading. */
            generatingTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (source?: TranscriptionSource | undefined) => { const transcriptionSource = source ?? ""; return __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.generatingTitle.0?c=12pm4bsmv02qk", defaultMessage: "{transcriptionSource, select, UPLOAD {Uploading Captions\u2026} other {Generating Transcript & Captions\u2026}}" }, { transcriptionSource: transcriptionSource }); });
                },
                enumerable: true,
                configurable: true
            },
            /** Label for loading spinner. */
            loadingLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.loadingLabel?c=57512ik3sq66", defaultMessage: "Loading" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for the speaker labeling toggle. */
            labelSpeakers: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.labelSpeakers?c=105nvladxjlms", defaultMessage: "Label Speakers" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Disclaimer text about transcript accuracy and auto detection. */
            transcriptsDisclaimer: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.transcriptsDisclaimer?c=1u9qcgwnccy0", defaultMessage: "<jsx>Accuracy may vary depending on the asset. <Break></Break> Selected language must match spoken language. If you select  <strong>Auto</strong>, the language detected may vary from what''s spoken.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), Break: (__children__: React.ReactNode) => <Break>{React.Children.toArray(__children__)}</Break>, strong: (__children__: React.ReactNode) => <strong>{React.Children.toArray(__children__)}</strong> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Disclaimer text for captions output. */
            captionsDisclaimer: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.captionsDisclaimer?c=2hoxrubk36h9t", defaultMessage: "Selected language will appear in Captions menu." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Retry button label. */
            retry: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.retry?c=24r9hk1amvd4v", defaultMessage: "Retry" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Dialog title. */
            title: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.title?c=2eg2mczcdktbf", defaultMessage: "Frame.io Transcription" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Warning shown when assets have no audio. */
            noAudioWarning: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "GenerateTranscriptionForm.noAudioWarning?c=1q3i3csg2y5u9", defaultMessage: "Some selected assets do not have audio and won''t be transcribed." }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps: Deps): void;
};
